<template>
  <div>
    <SearchToolbar
      v-if="showSearchBar"
      class="mb-6"
      :placeholder="'Recherche de point de vente par nom/url'"
      :initial-keywords="keywords"
      :available-filters="availableFilters"
      :selected-filters="selectedFilters"
      @update-keyword-search="updateKeywordSearch"
      @update-filter-search="updateFilterSearch"
    />
    <div>
      <Table :columns="columns" :data="formattedData" :is-row-clickable="true" @rowClick="redirectToStoreDetailsPage">
        <template #organizationName="{ row }">
          <TableLink
            v-if="row.organizationId"
            :to="`/organizations/${row.organizationId}`"
            data-test="organization-link"
          >
            {{ row.organizationName }}
          </TableLink>
        </template>
        <template #source="{ row }">
          <div
            v-if="row.source"
            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            :class="getSourceStyle(row.source)"
          >
            {{ $t(`store.source.${row.source}`) }}
          </div>
        </template>
        <template #status="{ row }">
          <FieldSpan
            :style-to-add="getStoreStatusFieldDisplayStyle(storeStatusOrSetup(row))"
            :value="storeStatusOrSetup(row)"
          />
        </template>
        <template #actions="{ row }">
          <TrashIcon
            v-if="isStoreSetup(row)"
            class="h-6 w-6 cursor-pointer"
            aria-hidden="true"
            @click.stop="openDisableStoreModal(row.id)"
          />
        </template>
      </Table>
    </div>
    <Pagination
      :currentPage="getCurrentPage"
      :totalPages="getTotalPages"
      :totalCount="getTotalCount"
      @changePage="handleChangePage"
    />
    <DisableModal
      :open="disableStoreModalOpen"
      :storeId="disableStoreId"
      @closeDisableStoreModal="disableStoreModalOpen = false"
      @disableStore="disableStore"
    />
  </div>
</template>
<script>
import DisableModal from "@/components/store/DisableModal.vue";
import { TrashIcon } from "@heroicons/vue/solid";
import { mapActions, mapState, mapGetters } from "vuex";
import { Pagination, SearchToolbar, Table, FieldSpan, isPresent, TableLink } from "@estaly/ui";

export default {
  components: {
    Pagination,
    SearchToolbar,
    FieldSpan,
    DisableModal,
    TrashIcon,
    Table,
    TableLink,
  },

  props: {
    organizationId: {
      type: [Number, String],
      default: null,
    },
    showSearchBar: {
      type: Boolean,
      default: true,
    },
  },

  async beforeMount() {
    this.loadQueryParams();
    await this.retrieveStores();
  },

  mounted() {
    this.availableFilters.status = this.getStatuses();
  },

  data() {
    return {
      keywords: null,
      availableFilters: {
        status: [],
      },
      selectedFilters: {
        status: [],
      },
      disableStoreModalOpen: false,
      disableStoreId: null,
      columns: [
        {
          label: "Id",
          field: "id",
          widthClass: "w-16",
        },
        {
          label: "Nom",
          field: "name",
          bold: true,
        },
        {
          label: "Raison sociale",
          field: "businessName",
        },
        {
          label: "Organisation",
          field: "organizationName",
          slot: "organizationName",
        },
        {
          label: "Canal de vente",
          field: "source",
          slot: "source",
        },
        {
          label: "Statut",
          field: "status",
          slot: "status",
        },
        {
          label: "",
          field: "actions",
          slot: "actions",
        },
      ],
    };
  },

  computed: {
    ...mapState("backoffice", ["isLoading"]),
    ...mapGetters("stores", [
      "getIndex",
      "getCurrentPage",
      "getTotalPages",
      "getTotalCount",
      "getStatuses",
      "getStoreStatusFieldDisplayStyle",
      "getSourceStyle",
      "storeStatusOrSetup",
      "isStoreSetup",
    ]),
    stores() {
      return this.getIndex || [];
    },
    status: {
      get() {
        return this.selectedFilters.status;
      },
      set(value) {
        if (!isPresent(value)) return (this.selectedFilters.status = []);

        this.selectedFilters.status = Array.isArray(value) ? value : [value];
      },
    },

    searchParams() {
      return {
        page: this.$route.query.page ? parseInt(this.$route.query.page, 10) : 1,
        keywords: this.keywords,
        status: this.status,
        organizationId: this.organizationId ? [this.organizationId] : [],
        includes: ["organization"],
        order: "-created_at",
      };
    },

    formattedData() {
      return this.stores.map((store) => ({
        id: store.id,
        name: store.salesChannel || (store.setupBy ? `Référent : ${store.setupBy}` : "—"),
        businessName: store.businessName,
        organizationName: store.organization?.name,
        organizationId: store.organization?.id,
        source: store.source,
        status: store.status,
        setupStatus: store.setupStatus,
        setupBy: store.setupBy,
      }));
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("backoffice", ["withLoader"]),
    ...mapActions("stores", ["fetchIndex", "updateStatus"]),
    isPresent,

    async retrieveStores() {
      await this.withLoader(async () => {
        await this.fetchIndex(this.searchParams);
      });
    },

    updateRouteQueryParams(page = null) {
      const query = {};
      if (page) query.page = page;
      if (this.keywords) query.keywords = this.keywords;
      if (this.status) query.status = this.status;
      this.$router.push({ query });
    },

    loadQueryParams() {
      this.keywords = this.$route.query.keywords;
      this.status = this.$route.query.status;
    },

    async handleChangePage(page) {
      this.updateRouteQueryParams(page);
      await this.retrieveStores();
    },

    redirectToStoreDetailsPage(store) {
      this.$router.push(`/stores/${store.id}`);
    },

    async updateKeywordSearch(keywords) {
      this.keywords = keywords;
      this.updateRouteQueryParams(1);
      await this.retrieveStores();
    },

    async updateFilterSearch(filterName, filterOptions) {
      this.selectedFilters[filterName] = filterOptions;
      this.updateRouteQueryParams(1);
      await this.retrieveStores();
    },

    openDisableStoreModal(storeId) {
      this.disableStoreId = storeId;
      this.disableStoreModalOpen = true;
    },

    async disableStore(storeId) {
      this.disableStoreModalOpen = false;

      await this.withLoader(async () => {
        const { success } = await this.updateStatus({ storeId, status: "inactive" });
        if (success) await this.notify({ category: "simple", type: "success", title: "Mise à jour effectuée" });
        await this.retrieveStores();
      });
    },
  },

  watch: {
    async "$route.query"() {
      this.loadQueryParams();
      await this.retrieveStores();
    },
  },
};
</script>
